import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { trackThemeToggle } from '../hooks/useAnalytics';
import './ThemeToggle.css';

const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme, isDark, isAutoMode, setAutoMode, setManualTheme } = useTheme();

  const handleToggle = () => {
    if (isAutoMode) {
      // If in auto mode, switch to manual mode with opposite theme
      const newTheme = theme === 'light' ? 'dark' : 'light';
      setManualTheme(newTheme);
      trackThemeToggle(newTheme);
    } else {
      // If in manual mode, just toggle
      toggleTheme();
      trackThemeToggle(theme === 'light' ? 'dark' : 'light');
    }
    
    // Add haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const handleLongPress = () => {
    // Long press to toggle auto mode
    if (isAutoMode) {
      // Switch to manual light mode
      setManualTheme('light');
      trackThemeToggle('light');
    } else {
      // Switch to auto mode
      setAutoMode();
      trackThemeToggle('auto');
    }
    
    // Add haptic feedback on mobile
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
  };

  const getToggleLabel = () => {
    if (isAutoMode) {
      return `Auto mode (${theme}) - tap to switch to manual ${theme === 'light' ? 'dark' : 'light'} mode, long press to disable auto mode`;
    }
    return `Manual ${isDark ? 'dark' : 'light'} mode - tap to switch to ${isDark ? 'light' : 'dark'}, long press to enable auto mode`;
  };

  return (
    <div className="theme-toggle-container">
      <button
        className={`theme-toggle ${isAutoMode ? 'auto-mode' : ''}`}
        onClick={handleToggle}
        onContextMenu={(e) => {
          e.preventDefault();
          handleLongPress();
        }}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          const startTime = Date.now();
          const startX = touch.clientX;
          const startY = touch.clientY;
          
          const handleTouchEnd = () => {
            const endTime = Date.now();
            const endX = touch.clientX;
            const endY = touch.clientY;
            
            const duration = endTime - startTime;
            const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
            
            if (duration > 500 && distance < 10) {
              handleLongPress();
            }
            
            document.removeEventListener('touchend', handleTouchEnd);
          };
          
          document.addEventListener('touchend', handleTouchEnd);
        }}
        aria-label={getToggleLabel()}
        title={getToggleLabel()}
        data-theme={theme}
      >
        <div className="theme-toggle-inner">
          {isDark ? (
            // Sun icon for dark mode (click to switch to light)
            <svg 
              className="theme-icon sun-icon" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            // Moon icon for light mode (click to switch to dark)
            <svg 
              className="theme-icon moon-icon" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
          
          {/* Auto mode indicator overlay */}
          {isAutoMode && (
            <div className="auto-mode-overlay">
              <svg className="auto-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v2"/>
                <path d="M12 20v2"/>
                <path d="m4.93 4.93 1.41 1.41"/>
                <path d="m17.66 17.66 1.41 1.41"/>
                <path d="M2 12h2"/>
                <path d="M20 12h2"/>
                <path d="m4.93 19.07 1.41-1.41"/>
                <path d="m17.66 6.34 1.41-1.41"/>
                <circle cx="12" cy="12" r="4"/>
              </svg>
            </div>
          )}
        </div>
      </button>
    </div>
  );
};

export default ThemeToggle; 